// Route: /offline — PWA offline fallback view.
// См. docs/ui-spec/system.offline.{desktop,mobile}.md

import { PAGE_CHROME_BAR } from "@studyhub/ui";
import { Button } from "@studyhub/ui/components/button";
import {
	IconCards,
	IconCheck,
	IconChevronRight,
	IconDatabase,
	IconLibrary,
	IconLoader2,
	IconMessage,
	IconNotebook,
	IconPointFilled,
	IconRefresh,
	IconSparkles,
	IconWifiOff,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { usePowerSyncStatus } from "@/hooks/use-powersync-status";
import { useCommerceVisible } from "@/lib/module-availability/commerce";
import { resolveOnlineStatus, subscribeOnlineStatus } from "@/lib/network-status";
import { connectPowerSync } from "@/lib/powersync/client";
import { usePowerSyncQuery } from "@/lib/powersync/use-query";
import { useAppUpdateStore } from "@/stores/app-update";
import { useSessionStore } from "@/stores/session";

export const Route = createFileRoute("/offline")({
	component: OfflinePage,
	pendingComponent: PagePendingFallback,
});

type OfflineState = "offline" | "checking" | "restored";

interface CachedCounts {
	materials: number;
	notes: number;
	flashcards: number;
	tutorChats: number;
}

/**
 * Реальные офлайн-счётчики из локальной SQLite (PowerSync). Запросы реактивны —
 * перерисуются по мере дозагрузки sync-стримов.
 */
function useOfflineCachedCounts(userId: string): CachedCounts {
	const materials = usePowerSyncQuery<{ c: number }>(
		`SELECT COUNT(*) AS c FROM materials WHERE user_id = ? AND deleted_at IS NULL`,
		[userId],
	);
	const notes = usePowerSyncQuery<{ c: number }>(
		`SELECT COUNT(*) AS c FROM notes WHERE user_id = ? AND deleted_at IS NULL`,
		[userId],
	);
	const flashcards = usePowerSyncQuery<{ c: number }>(
		`SELECT COUNT(*) AS c FROM flashcards f
       INNER JOIN flashcard_sets s ON f.set_id = s.id
       WHERE s.user_id = ? AND s.deleted_at IS NULL AND f.deleted_at IS NULL`,
		[userId],
	);
	const tutorChats = usePowerSyncQuery<{ c: number }>(
		`SELECT COUNT(*) AS c FROM tutor_sessions WHERE user_id = ? AND deleted_at IS NULL`,
		[userId],
	);
	return {
		materials: materials.data?.[0]?.c ?? 0,
		notes: notes.data?.[0]?.c ?? 0,
		flashcards: flashcards.data?.[0]?.c ?? 0,
		tutorChats: tutorChats.data?.[0]?.c ?? 0,
	};
}

function OfflinePage(): React.ReactElement {
	const { t, i18n } = useTranslation("offline");
	const commerceVisible = useCommerceVisible();
	const user = useSessionStore((s) => s.user);
	const { lastSyncedAt } = usePowerSyncStatus();
	const cachedCounts = useOfflineCachedCounts(user?.id ?? "");
	const qc = useQueryClient();
	const navigate = useNavigate();
	const appUpdateAvailable = useAppUpdateStore((s) => s.available);

	const [state, setState] = useState<OfflineState>("checking");

	useEffect(() => {
		return subscribeOnlineStatus((online) => setState(online ? "restored" : "offline"));
	}, []);

	const handleRetry = (): void => {
		setState("checking");
		void resolveOnlineStatus().then((online) => setState(online ? "restored" : "offline"));
	};

	const handleSoftReconnect = (): void => {
		void connectPowerSync().catch(() => undefined);
		void qc.refetchQueries({ type: "active" });
		void navigate({ to: "/" });
	};

	return (
		<div className="min-h-dvh flex flex-col bg-background">
			<header className="sticky top-0 z-30 h-14 border-b border-border bg-background">
				<div className={PAGE_CHROME_BAR}>
					<Link to="/" className="flex items-center gap-2 text-lg font-bold cursor-pointer">
						<IconSparkles size={20} stroke={1.75} className="text-primary" /> StudyHub
					</Link>
				</div>
			</header>

			<main className="flex-1 flex flex-col items-center justify-center px-4 py-12 text-center">
				<div
					aria-hidden="true"
					className={
						state === "restored"
							? "size-12 inline-flex items-center justify-center rounded-md bg-success/15 text-success"
							: state === "checking"
								? "size-12 inline-flex items-center justify-center rounded-md bg-primary/15 text-primary"
								: "size-12 inline-flex items-center justify-center rounded-md bg-warning/15 text-warning"
					}
				>
					{state === "restored" ? (
						<IconCheck size={32} stroke={1.5} />
					) : state === "checking" ? (
						<IconLoader2 size={32} stroke={1.5} className="animate-spin" />
					) : (
						<IconWifiOff size={32} stroke={1.5} />
					)}
				</div>

				<h1 className="text-2xl lg:text-3xl font-semibold mt-6">
					{state === "restored" ? t("restored") : state === "checking" ? t("checking") : t("title")}
				</h1>

				{state === "offline" && (
					<p className="text-sm lg:text-md text-muted-foreground mt-3 max-w-md">
						{user ? t("leadAuth", { name: user.name.split(" ")[0] }) : t("leadAnonymous")}
					</p>
				)}

				{lastSyncedAt && (
					<p className="text-xs text-muted-foreground mt-6 tabular-nums" role="status">
						{t("lastSync")} ·{" "}
						{new Intl.DateTimeFormat(i18n.language, {
							hour: "2-digit",
							minute: "2-digit",
						}).format(lastSyncedAt)}
					</p>
				)}

				<div className="flex flex-col lg:flex-row items-center justify-center gap-3 mt-6 w-full max-w-md">
					{state === "restored" ? (
						<>
							<Button
								size="lg"
								className="cursor-pointer h-11 w-full lg:w-auto"
								onClick={handleSoftReconnect}
							>
								<IconRefresh size={16} stroke={1.75} /> {t("continueOnline")}
							</Button>
							{appUpdateAvailable ? (
								<Button
									variant="outline"
									size="lg"
									className="cursor-pointer h-11 w-full lg:w-auto"
									onClick={() => window.location.reload()}
								>
									{t("restoredAuto")}
								</Button>
							) : null}
						</>
					) : (
						<>
							<Button
								size="lg"
								className="cursor-pointer h-11 w-full lg:w-auto"
								onClick={handleRetry}
								disabled={state === "checking"}
							>
								{state === "checking" ? (
									<IconLoader2 size={16} className="animate-spin" stroke={1.75} />
								) : (
									<IconRefresh size={16} stroke={1.75} />
								)}
								{state === "checking" ? t("checking") : t("retry")}
							</Button>
							<Button
								variant="outline"
								size="lg"
								className="cursor-pointer h-11 w-full lg:w-auto"
								asChild
							>
								<Link to="/">
									<IconDatabase size={16} stroke={1.75} /> {t("openCached")}
								</Link>
							</Button>
						</>
					)}
				</div>

				{user && state === "offline" && (
					<section className="mt-12 max-w-md w-full text-left">
						<h2 className="text-2xs uppercase tracking-wider font-semibold text-muted-foreground text-center">
							{t("cached.available")}
						</h2>
						<ul className="mt-4 space-y-1">
							{(
								[
									{
										icon: IconLibrary,
										count: cachedCounts.materials,
										label: "materials",
										to: "/materials",
									},
									{
										icon: IconNotebook,
										count: cachedCounts.notes,
										label: "notes",
										to: "/notes",
									},
									{
										icon: IconCards,
										count: cachedCounts.flashcards,
										label: "flashcards",
										to: "/flashcards",
									},
									{
										icon: IconMessage,
										count: cachedCounts.tutorChats,
										label: "tutorChats",
										to: "/tutor",
									},
								] as const
							)
								// Ссылки ведут в соответствующие разделы: раньше все четыре
								// строки вели на "/", и клик по «28 материалов» открывал дашборд.
								.map(({ icon: Icon, count, label, to }) => (
									<li key={label}>
										<Link
											to={to}
											className="flex items-center gap-3 px-4 py-3 rounded-md hover:bg-muted transition-colors cursor-pointer"
										>
											<Icon size={16} stroke={1.75} className="text-muted-foreground" />
											<span className="text-sm flex-1">{t(`cached.${label}`, { count })}</span>
											<span className="tabular-nums text-xs text-muted-foreground">{count}</span>
											<IconChevronRight size={12} className="text-muted-foreground/60" />
										</Link>
									</li>
								))}
						</ul>
					</section>
				)}

				{!user && state === "offline" && (
					<section className="mt-12 max-w-md w-full grid grid-cols-1 sm:grid-cols-2 gap-6 text-left">
						<div>
							<h2 className="text-2xs uppercase tracking-wider font-semibold text-success">
								{t("limits.works")}
							</h2>
							<ul className="mt-2 space-y-1 text-sm">
								<li className="flex items-center gap-1.5">
									<IconCheck size={14} stroke={2} className="shrink-0 text-success" />
									{t("limits.notes")}
								</li>
								<li className="flex items-center gap-1.5">
									<IconCheck size={14} stroke={2} className="shrink-0 text-success" />
									{t("limits.flashcards")}
								</li>
								<li className="flex items-center gap-1.5">
									<IconCheck size={14} stroke={2} className="shrink-0 text-success" />
									{t("limits.recordings")}
								</li>
							</ul>
						</div>
						<div>
							<h2 className="text-2xs uppercase tracking-wider font-semibold text-muted-foreground">
								{t("limits.fails")}
							</h2>
							<ul className="mt-2 space-y-1 text-sm text-muted-foreground">
								<li className="flex items-center gap-1.5">
									<IconPointFilled size={10} className="shrink-0 text-muted-foreground/50" />
									{t("limits.ai")}
								</li>
								<li className="flex items-center gap-1.5">
									<IconPointFilled size={10} className="shrink-0 text-muted-foreground/50" />
									{t("limits.search")}
								</li>
								{/* Строка про оплату — только там, где оплата вообще видна
								    (в приложении из App Store её нет, guideline 3.1.1). */}
								{commerceVisible && (
									<li className="flex items-center gap-1.5">
										<IconPointFilled size={10} className="shrink-0 text-muted-foreground/50" />
										{t("limits.billing")}
									</li>
								)}
							</ul>
						</div>
					</section>
				)}
			</main>
		</div>
	);
}
